// TMOS Platform Settings Service
// Date: January 24, 2026
// Purpose: Frontend service for platform settings management

import api from './api';

export type SettingType = 'string' | 'number' | 'boolean' | 'json' | 'password';
export type SettingCategory = 'logger' | 'smtp' | 'website' | 'system' | 'general';

export interface PlatformSetting {
  id: string;
  setting_key: string;
  setting_value: string | null;
  setting_type: SettingType;
  category: SettingCategory;
  description: string | null;
  is_sensitive: boolean;
  is_system: boolean;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  updated_by: string | null;
}

export interface CreateSettingRequest {
  setting_key: string;
  setting_value: string | null;
  setting_type: SettingType;
  category: SettingCategory;
  description?: string;
  is_sensitive?: boolean;
  is_system?: boolean;
}

export interface UpdateSettingRequest {
  setting_value?: string | null;
  description?: string;
  is_active?: boolean;
}

class PlatformSettingsService {
  /**
   * Get all settings (optionally filtered by category)
   */
  async getSettings(category?: string): Promise<PlatformSetting[]> {
    const params = category ? { category } : {};
    const response = await api.get('/platform/settings', { params });
    return response.data || [];
  }

  /**
   * Get single setting by key
   */
  async getSetting(key: string): Promise<PlatformSetting | null> {
    const response = await api.get(`/platform/settings/${key}`);
    return response.data || null;
  }

  /**
   * Get setting value directly (returns just the value, not the full setting object)
   */
  async getSettingValue(key: string): Promise<any> {
    const setting = await this.getSetting(key);
    return setting?.setting_value || null;
  }

  /**
   * Create new setting
   */
  async createSetting(data: CreateSettingRequest): Promise<PlatformSetting> {
    const response = await api.post('/platform/settings', data);
    return response.data;
  }

  /**
   * Update existing setting
   */
  async updateSetting(key: string, updates: UpdateSettingRequest): Promise<PlatformSetting> {
    const response = await api.put(`/platform/settings/${key}`, updates);
    return response.data;
  }

  /**
   * Validate SMTP connection then save all email settings and reload transporter.
   */
  async updateSmtpSettings(values: {
    SMTP_HOST: string;
    SMTP_PORT: string | number;
    SMTP_USER: string;
    SMTP_PASS?: string;
    SMTP_SECURE: string | boolean;
    SMTP_FROM?: string;
    SMTP_FROM_NAME?: string;
  }): Promise<{
    success: boolean;
    message: string;
    smtp_status: string;
    settings: PlatformSetting[];
  }> {
    const response = await api.put('/platform/settings/smtp', values);
    return response.data;
  }

  /**
   * Deactivate a setting
   */
  async deactivateSetting(key: string): Promise<void> {
    await api.patch(`/platform/settings/${key}/deactivate`);
  }
}

export const platformSettingsService = new PlatformSettingsService();

